import React from 'react'
import i18next from 'i18next'
import hash from 'object-hash'
import { Trans } from 'react-i18next'
import { connect } from 'react-redux'
import { Modal, Button, Card, Badge } from 'react-bootstrap'
import { parseISO, format } from 'date-fns'
import ActionsDropdown from 'src/components/system_wide/actions_dropdown'
import SearchButton from 'src/components/system_wide/search_button'
import CheckboxFilter from 'src/components/system_wide/checkbox_filter'
import FilterGroup from 'src/components/system_wide/filter_group'
import ResponsiveTable from 'src/components/system_wide/responsive_table'
import { WithCapabilitiesWrapper, checkCapabilities } from 'src/enhancers/with_capabilities'
import DocumentForm from './document_form'
import {
  updateDocument,
  setCurrentDocument,
  deleteDocument,
  toggleShowForm,
  getDocuments,
  resetDocuments,
  resetCurrentDocument,
} from 'src/store/document'
import { getAllBrands } from 'src/store/brand'
import { downloadFile } from 'src/components/system_wide/file_downloader_button'
import { showConfirmDialog } from 'src/components/system_wide/confirm_dialog'
import IconButton from 'src/components/system_wide/icon_button'
import UsersDocumentDetailsForm from './users_document_details_form'
import { getAllUsers } from 'src/store/users'
import { getAllRoles } from 'src/store/roles_manager'

const { t } = i18next

const mstp = state => {
  let { business_role_priority } = state.userInfo
  let { total, documents, show_form, current_document } = state.documents
  let { all_brands: brands } = state.brands
  let { all_users: users } = state.users
  let { all_roles: roles } = state.rolesManager
  const manage_documents = checkCapabilities(['manage_documents'])
  return {
    total,
    documents,
    show_form,
    current_document,
    business_role_priority,
    manage_documents,
    brands,
    users,
    roles,
  }
}

class ManageDocuments extends React.Component {
  state = {
    show_modal: false,
    filter: '',
    filtered_brands: [],
    current_document: { id: null, description: '' },
    ready: false,
    mode: '',

    filters_hash: '',
  }

  initialFiltersHash = ''
  tableRef = React.createRef()
  usersDocumentDetailsModalRef = React.createRef()

  async componentDidMount() {
    if (this.props.manage_documents) {
      await this.props.dispatch(getAllBrands())
      await this.props.dispatch(getAllUsers({ filtered_roles_priority: [2, 3, 4] }))
      await this.props.dispatch(getAllRoles())
    }
    this.setState({ filtered_brands: this.props.brands.map(b => b.id), ready: true }, () => {
      let { filtered_brands } = this.state
      filtered_brands.sort()

      this.initialFiltersHash = hash({
        filtered_brands: filtered_brands,
      })
      this.updateFiltersHash()
      this.tableRef?.current?.refreshData()
    })
  }

  componentWillUnmount() {
    this.props.dispatch(resetDocuments())
  }

  updateFiltersHash = () => {
    let { filtered_brands } = this.state
    filtered_brands.sort()
    this.setState({ filters_hash: hash({ filtered_brands }) })
  }

  handleDownloadFile = async ({ filename, name }) => {
    try {
      await downloadFile({
        url: `api/document/download/${filename}`,
        filename: name,
      })
      this.tableRef?.current?.refreshData()
    } catch (err) {
      console.log(err)
    }
  }

  actions = [
    {
      name: 'download',
      icon: 'fa fa-download',
      text: t('actions.download', 'Scarica'),
      variant: 'outline-primary',
      onClick: async rowData => {
        await this.handleDownloadFile(rowData)
      },
    },
    {
      name: 'edit',
      icon: 'fa fa-edit',
      text: t('actions.edit_visibility', 'Cambia visibilità'),
      visible: this.props.manage_documents,
      variant: 'outline-warning',
      onClick: async rowData => {
        let { id, description, users, brands } = rowData
        await this.props.dispatch(
          setCurrentDocument({
            id,
            description,
            selected_users: users.map(u => u.id),
            selected_brands: brands.map(b => b.id),
          })
        )
        this.setState({ mode: 'edit' })
        this.onToggleForm()
      },
    },
    {
      name: 'trash',
      icon: 'fa fa-trash-alt',
      text: t('actions.delete', 'Elimina'),
      variant: 'outline-danger',
      visible: this.props.manage_documents,
      onClick: rowData => {
        let { id, name } = rowData
        showConfirmDialog(
          'Elimina documento',
          <div>
            Per procedere alla cancellazione di <b>{name}</b> click su Elimina
          </div>,
          async () => {
            await this.props.dispatch(deleteDocument(id))
            this.tableRef?.current?.refreshData()
          },
          () => {},
          'primary',
          'Elimina'
        )
      },
    },
  ]

  actionsRenderer = ({ rowData }) => {
    if (this.props.business_role_priority !== 1) {
      return <IconButton variant="secondary" icon="fa fa-download" onClick={() => this.handleDownloadFile(rowData)} />
    } else {
      return <ActionsDropdown rowData={rowData} actions={this.actions} />
    }
  }

  columns = [
    {
      title: '',
      className: 'd-none d-sm-block text-center',
      style: { width: '6%', minWidth: '3rem' },
      data: this.actionsRenderer,
    },
    {
      title: t('components.documents.name', 'Nome'),
      className: 'd-none d-sm-block flex-fill',
      style: { width: '20%' },
      sortingKey: 'name',
      sortable: true,
      data: ({ rowData }) => {
        let is_new = this.props.business_role_priority !== 1 && !rowData.downloaded_at
        return (
          <>
            {is_new === true && (
              <Badge role="button" bg="success" className="me-2">
                <span>
                  <Trans i18nKey="components.documents.new_document">Nuovo</Trans>
                </span>
              </Badge>
            )}
            {rowData.name}
          </>
        )
      },
    },
    {
      title: t('components.documents.description', 'Descrizione'),
      className: 'd-none d-lg-block',
      style: { width: '25%' },
      sortable: true,
      data: 'description',
    },
    {
      title: t('components.documents.brands', 'Brands'),
      className: 'd-none d-xxl-block',
      style: { width: '25%' },
      sortable: true,
      data: ({ rowData }) => {
        return <div className="text-truncate">{(rowData?.brands ?? []).map(b => b.name).join(', ')}</div>
      },
    },
    {
      title: t('components.documents.created_at', 'Creato il'),
      className: 'd-none d-md-block d-lg-block flex-fill text-center',
      style: { width: '12%' },
      sortable: true,
      sortingKey: 'created_at',
      data: ({ rowData }) => {
        return format(parseISO(rowData.created_at), 'yyyy/MM/dd HH:mm')
      },
    },
    {
      name: 'downloaded_at',
      title: t('components.documents.downloaded_at', 'Scaricato il'),
      className: 'd-none d-md-block d-lg-block flex-fill text-center',
      style: { width: '12%' },
      sortable: true,
      sortingKey: 'downloaded_at',
      data: ({ rowData }) => {
        return rowData.downloaded_at !== null ? format(parseISO(rowData.downloaded_at), 'yyyy/MM/dd HH:mm') : ''
      },
    },
    {
      name: 'users',
      title: t('components.documents.users', 'Utenti'),
      className: 'd-none d-md-block d-lg-block flex-fill text-center',
      style: { width: '12%' },
      sortable: false,
      data: ({ rowData }) => {
        return (
          <Badge role="button" bg="success" onClick={this.showUsersDocumentDetails(rowData.users)}>
            <span className="me-2">{rowData.users.length}</span>
            <i className="fa fa-users mb-0" />
          </Badge>
        )
      },
    },
    {
      title: t('components.documents.detail', 'Dettagli'),
      className: 'd-block d-sm-none flex-fill',
      style: { width: '15%' },
      data: ({ rowData }) => this.renderDetails(rowData),
    },
  ]

  renderDetails(rowData) {
    const { name, description, created_at, downloaded_at, brands, users } = rowData
    let is_new = this.props.business_role_priority !== 1 && !downloaded_at

    return (
      <Card className="shadow-lg border-0 rounded-lg">
        <Card.Body className="pb-2 pt-2">
          <h5 className="fw-bold text-primary">
            {this.props.business_role_priority === 1 && (
              <Badge role="button" bg="success" className="me-2" onClick={this.showUsersDocumentDetails(users)}>
                <span className="me-2">{users.length}</span>
                <i className="fa fa-users mb-0" />
              </Badge>
            )}
            {is_new === true && (
              <Badge role="button" bg="success" className="me-2">
                <span>
                  <Trans i18nKey="components.documents.new_document">Nuovo</Trans>
                </span>
              </Badge>
            )}
            {name}
          </h5>
          <p className="text-muted mb-1">
            <i className="fas fa-calendar-alt me-2"></i>
            {format(parseISO(created_at), 'yyyy/MM/dd HH:mm')}
          </p>
          {this.props.business_role_priority !== 1 && (
            <p className="text-muted mb-1">
              <i className="fas fa-download me-2"></i>
              {downloaded_at ? format(parseISO(downloaded_at), 'yyyy/MM/dd HH:mm') : 'Non ancora scaricato'}
            </p>
          )}
          <p className="text-muted mb-1">
            <i className="fas fa-tag me-2"></i>
            {(brands ?? []).map(b => b.name).join(', ')}
          </p>
          <p className="text-muted mb-1">
            <i className="fas fa-info-circle me-2"></i>
            {description}
          </p>
        </Card.Body>

        {/* Footer con azioni */}
        <Card.Footer className="text-center bg-light pb-3 pt-3">
          <div className="d-flex justify-content-around flex-wrap">
            {this.actions
              .filter(a => {
                if (a.name === 'separator') {
                  return false
                }
                if (typeof a.visible === 'function') {
                  return a.visible(rowData, a)
                }
                return a?.visible ?? true
              })
              .map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant}
                  size="sm"
                  title={action.text}
                  disabled={
                    typeof action.disabled === 'function' ? action.disabled(rowData) : action?.disabled ?? false
                  }
                  onClick={() => action.onClick(rowData)}>
                  <i className={`${action.icon} me-0`}></i>
                </Button>
              ))}
          </div>
        </Card.Footer>
      </Card>
    )
  }

  getColumns = () => {
    let columns = [...this.columns]

    if (this.props.business_role_priority === 1) {
      columns = columns.filter(col => col.name !== 'downloaded_at')
    }

    if (this.props.business_role_priority !== 1) {
      columns = columns.filter(col => col.name !== 'users')
    }

    return columns
  }

  onFilterChange = filter => this.setState({ filter })

  getData = async (index = 0, chunkSize, sortData, filter) => {
    let { filtered_brands, ready } = this.state
    if (!ready) return
    if (this.props.manage_documents === false) {
      filtered_brands = []
    }
    await this.props.dispatch(getDocuments({ offset: index, limit: chunkSize, sortData, filter, filtered_brands }))
  }

  saveDocument = async document => {
    if (document.id !== null) {
      await this.props.dispatch(updateDocument({ document }))
    }
    this.tableRef.current.refreshData()
  }

  onToggleForm = () => {
    if (this.props.show_form === true) {
      this.props.dispatch(resetCurrentDocument())
      this.tableRef.current.refreshData()
    }
    this.props.dispatch(toggleShowForm())
  }

  onCancel = () => {
    this.setState({ show_modal: false, mode: '' })
  }

  onConfirmDelete = async () => {
    await this.props.dispatch(deleteDocument({ tenant: this.props.tenant, id: this.props.current_document.id }))
    this.tableRef.current.refreshData()
    this.setState({ show_modal: false })
  }
  onChangeFilter = name => values => {
    this.setState({ [name]: values }, () => {
      this.updateFiltersHash()
      this.tableRef?.current?.refreshData()
    })
  }

  showAddDocument = () => {
    this.setState({ mode: 'edit' })
    this.onToggleForm()
  }

  showUsersDocumentDetails = users => () => {
    this.setState({ mode: 'details' })
    this.props.dispatch(setCurrentDocument({ users }))
    this.onToggleForm()
  }

  onResetFilters = () => {
    this.setState(
      {
        filtered_brands: this.props.brands.map(b => b.id),
      },
      () => {
        this.tableRef?.current?.refreshData()
        this.updateFiltersHash()
      }
    )
  }

  render() {
    let { show_modal, filtered_brands, mode, filters_hash } = this.state
    let menuContent = DocumentForm
    let menuContentProps = {
      title: t('components.documents.upload_document', 'Carica documento'),
      document: this.props.current_document,
      users: this.props.users,
      roles: this.props.roles,
      brands: this.props.brands,
      onSave: this.saveDocument,
      closeForm: this.onToggleForm,
      forceUseModal: true,
    }

    if (mode === 'details') {
      menuContent = UsersDocumentDetailsForm
      menuContentProps = {
        title: t('components.documents.users', 'Utenti'),
        users: this.props.current_document?.users ?? [],
        closeForm: this.onToggleForm,
        forceUseModal: true,
      }
    }
    return (
      <div className="card card-body container-fluid flex-fill d-flex flex-column px-0 pb-0 pt-2 mb-0">
        <div className="d-flex flex-column flex-fill">
          <div className="toolbar px-2">
            <WithCapabilitiesWrapper has={['manage_documents']}>
              <Button onClick={this.showAddDocument} disabled={this.props.show_form}>
                <i className="fas fa-plus fa-fw text-start me-0 ps-1 ps-sm-0" />
                <span className="d-none d-sm-inline">
                  <Trans i18nKey="buttons.add_document">Aggiungi documento</Trans>
                </span>
              </Button>
            </WithCapabilitiesWrapper>
            <WithCapabilitiesWrapper has={['manage_documents']}>
              <FilterGroup
                className="toolbar-item"
                showResetFilters={this.initialFiltersHash !== filters_hash}
                onResetFilters={this.onResetFilters}>
                <CheckboxFilter
                  label={t('filter_brands', 'Brand')}
                  items={this.props.brands}
                  filteredItems={filtered_brands}
                  onChangeFilter={this.onChangeFilter('filtered_brands')}
                  showSearch={true}
                />
              </FilterGroup>
            </WithCapabilitiesWrapper>
            <div className="toolbar-spacer"></div>
            <SearchButton
              value={this.state.filter}
              onChange={this.onFilterChange}
              disabled={this.props.show_form}
              className="float-md-end"
            />
          </div>

          <ResponsiveTable
            ref={this.tableRef}
            className="flex-fill"
            data={this.props.documents}
            getData={this.getData}
            totalRecords={this.props.total}
            columns={this.getColumns()}
            showMenu={this.props.show_form}
            modalSize={'xl'}
            menuContent={menuContent}
            menuContentProps={menuContentProps}
            rowClasses={rowData => (this.props.business_role_priority !== 1 && !rowData.downloaded_at ? 'fw-bold' : '')}
            filter={this.state.filter}
          />
        </div>

        <Modal backdrop="static" centered show={show_modal} onHide={this.onCancel}>
          <Modal.Header>
            <Modal.Title>
              <Trans i18nKey="tenant.components.categories.delete_account_modal.title">Elimina account</Trans>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              <Trans
                i18nKey="tenant.components.categories.delete_account_modal.description"
                values={{ document: this.props.current_document?.name }}
                defaults={`Per procedere all'eliminazione dell'utente <b>{{document}}</b>, cliccare sul bottone "Elimina"`}
                components={{ 1: <b /> }}
              />
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.onCancel} variant="secondary">
              <Trans i18nKey="buttons.cancel">Annulla</Trans>
            </Button>
            <Button onClick={this.onConfirmDelete} variant="danger">
              <Trans i18nKey="buttons.delete">Elimina</Trans>
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    )
  }
}
export default connect(mstp)(ManageDocuments)
